/**
 * Centralized date formatting utilities.
 * All display dates should use these helpers to keep formats consistent.
 */

/**
 * "Jan 15, 2026"
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * "Jan 15, 2026 at 3:30 PM"
 */
export const formatDateTime = (dateStr) => {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  const date = d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${date} at ${time}`;
};

/**
 * "3:30 PM"
 */
export const formatTime = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * "Mon, Jan 15"  —  short date for cards / schedules
 */
export const formatShortDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Relative time: "Just now", "5 min ago", "3 hours ago", "2 days ago",
 * then falls back to formatDate.
 */
export const formatRelativeTime = (dateStr) => {
  if (!dateStr) return 'N/A';
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHr < 24) return `${diffHr} hour${diffHr > 1 ? 's' : ''} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
  return formatDate(dateStr);
};

/**
 * Calendar-style breakdown: { day: "15", month: "Jan", weekday: "Monday" }
 */
export const getCalendarParts = (dateStr) => {
  if (!dateStr) return { day: '--', month: '--', weekday: '--' };
  const d = new Date(dateStr);
  return {
    day: d.getDate().toString(),
    month: d.toLocaleDateString('en-US', { month: 'short' }),
    weekday: d.toLocaleDateString('en-US', { weekday: 'long' }),
  };
};

/**
 * "YYYY-MM-DD"  — for HTML date input values
 */
export const toInputDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toISOString().split('T')[0];
};

/**
 * "YYYY-MM-DDTHH:MM" — for HTML datetime-local input values
 */
export const toInputDateTime = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toISOString().slice(0, 16);
};
